import {isNullOrEmpty, translation} from "@2d-front-monorepo/core";
import {MvvmButton} from "@2d-front-monorepo/design-library";
import {
	CreateOrSelectResidencyModel, DefaultParkingCategory, getResidencyConfiguration,
	IntegraZone,
	isZoneChange,
	Residencies,
	ResidencyId,
	ResidencyResult,
	ResidencyUses,
	ResidencyWorkflowOptions,
	toFormAttachments,
	ZoneId,
	Zones
} from "@2d-front-monorepo/permits-business";

import {PermitTranslations} from "@2d-front-monorepo/shared-translations";

import {JSX, useCallback, useMemo} from "react";
import {PermitsBackButton} from "../../../components/PermitsBackButton";
import {getGlobalContext} from "../../../hooks/getGlobalContext";
import {getHttpClient} from "../../../hooks/getHttpClient";
import {ReloadEntities, reloadPermits} from "../../../hooks/reloadPermits";
import {PrivateRoutes} from "../../../routes";
import {CreateResidencyProps} from "../../Registrations";
import {useNavigate} from "react-router";
import ResidencyBase from "../ResidencyBase";

type FooterProps = {
	state: CreateOrSelectResidencyModel;
	residencies: ResidencyResult[] | undefined;
	zoneIdWarning: ZoneId | undefined;
	nextDisabled?: boolean;
}

function needsDocumentationUpdate(state: CreateOrSelectResidencyModel, residencies: ResidencyResult[] | undefined, zoneIdWarning: ZoneId | undefined): boolean {
	const globalContext = getGlobalContext();
	return globalContext.installation.configuration.ui.forceDocumentationUpdate
		&& isZoneChange(state, residencies, zoneIdWarning);
}

function Footer(props: FooterProps): JSX.Element {
	const {state, residencies, zoneIdWarning, nextDisabled} = props;

	const nextLabel = needsDocumentationUpdate(state, residencies, zoneIdWarning)
		? PermitTranslations.next
		: PermitTranslations.save_changes

	return (
		<div className="form__buttons form__buttons--bottom">
			<MvvmButton kind="primary" disabled={nextDisabled}>{translation(nextLabel)}</MvvmButton>
			<span></span>
			<PermitsBackButton/>
		</div>
	);
}

export default function Create(props: CreateResidencyProps): JSX.Element {
	const {holder, permit, permits, residency, behavior} = props;
	const navigate = useNavigate();
	const globalContext = getGlobalContext();

	const residencyUses = behavior?.type ?? residency?.uses ?? ResidencyUses.None;
	const configuration = getResidencyConfiguration(globalContext.installation, residencyUses, permit?.category);
	const availableResidencies = useMemo(() => {
		return behavior == null
			? permits.residencies.filter(v => v.uses === residencyUses)
			: Residencies.getAll(holder, behavior.ids);

	}, [permits.residencies, residencyUses, holder, behavior]);

	const zoneIdWarning = residency?.zoneId;

	const initialState = useMemo<CreateOrSelectResidencyModel>(() => {
		return {
			residencyId: "create",
			arguments: {
				address: {},
				uses: residencyUses,
				zoneId: ("" as ZoneId),
				attachments: [],
				customData: []
			},
			customData: {},
			attachments: {}
		}
	}, [residency, residencyUses]);

	const initialZones = useMemo<IntegraZone[]>(() => {
		return isNullOrEmpty(initialState.arguments.zoneId) ? [] : [Zones.get(globalContext.installation, initialState.arguments.zoneId)];
	}, [initialState.arguments.zoneId, globalContext.installation]);

	const onSubmit = useCallback(async (result: CreateOrSelectResidencyModel) => {
		const httpClient = getHttpClient();
		const args = result.arguments;
		const isCreated = result.residencyId === "create";
		const category = permit?.category ?? DefaultParkingCategory;
		const attachments = await toFormAttachments(httpClient, result.attachments);

		let newResidencyId: ResidencyId;

		if (isCreated) {
			newResidencyId = await httpClient.residencies.create({
				attachments: attachments,
				customData: args.customData,
				zoneId: isNullOrEmpty(args.zoneId) ? undefined : args.zoneId,
				address: args.address,
				suffix: args.suffix,
				uses: args.uses,
				category: category
			});

		} else {
			newResidencyId = result.residencyId;
			await httpClient.residencies.updateAttachments(newResidencyId, {
				attachments: attachments,
				category: category
			});
		}

		if (isCreated) {
			await reloadPermits(ReloadEntities.Holder);
		}

		if (permit != null && residency != null) {
			await httpClient.permits.replaceResidency(permit.id, {
				residencyId: newResidencyId
			});

			if (needsDocumentationUpdate(result, availableResidencies, zoneIdWarning)) {
				navigate(`${PrivateRoutes.documentsExpired}?residencyId=${residency.id}`);

			} else {
				window.history.back();
			}

		} else {
			navigate(PrivateRoutes.permitsHome);
		}
	}, [permit, residency]);

	const options = behavior?.options
		?? (ResidencyWorkflowOptions.UseAutocomplete | ResidencyWorkflowOptions.UseRemoteZones);

	return <>
		<h1>{translation(PermitTranslations.residency)}</h1>
		<hr/>
		<div className="layout vertical gap">
			<ResidencyBase
				initialState={initialState}
				initialZones={initialZones}
				residencyUses={residencyUses}
				configuration={configuration}
				residencies={availableResidencies}
				createEnabled={behavior?.addNew ?? true}
				onSubmit={onSubmit}
				zoneIdWarning={zoneIdWarning}
				options={options}
				footer={(state) => <Footer state={state} residencies={availableResidencies} zoneIdWarning={zoneIdWarning}/>}
			/>
		</div>
	</>;
}
